import React from "react";
import NavbarComponent from "../navbar/NavbarComponent";
import NavbarViewModel from "../../view-model/navbar/NavbarViewModel";
import TableViewModel from "../../view-model/dashboard/TableViewModel";
import ProductItem from "../../../domain/models/dashboard/ProductItem";
import BaseView from "../BaseView";
import LoadingComponent from "../../../util/component/LoadingComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TableSchema from "../../../util/Table/models/TableSchema";
import Table from "../../../util/Table";
import StringUtils from "../../../util/StringUtils";
import { Toast, Modal } from "react-bootstrap";
import CountryItem from "../../../domain/models/country/CountryItem";
import FooterComponent from "../footer/FooterComponent";
import BestSellerItem from "../../../domain/models/dashboard/BestSellerItem";

interface Props {
  navbarViewModel: NavbarViewModel;
  tableViewModel: TableViewModel;
}

interface State {
  productSearchQuery: string;
  isProductsSearching: boolean;

  isShowSearchFinished: boolean;
  items: ProductItem[];

  displayingItems: ProductItem[];
  selectedImage?: string;

  lastSearchCountry?: CountryItem;
  bestSellers: BestSellerItem[];
}

export default class TableViewComponent extends React.Component<Props, State> implements BaseView {
  private tableViewModel: TableViewModel;

  public constructor(props: Props) {
    super(props);
    this.tableViewModel = props.tableViewModel;

    this.state = {
      productSearchQuery: this.tableViewModel.productSearchQuery,
      isProductsSearching: this.tableViewModel.isProductsSearching,

      isShowSearchFinished: this.tableViewModel.isShowSearchFinished,
      items: this.tableViewModel.items,

      displayingItems: this.tableViewModel.displayingItems,
      lastSearchCountry: this.tableViewModel.lastSearchCountry,

      bestSellers: this.tableViewModel.bestSellers,
    };
  }

  public componentDidMount = (): void => {
    this.tableViewModel.attachView(this);
  };

  public componentWillUnmount = (): void => {
    this.tableViewModel.detachView(this);
  };

  public onViewModelChanged = (): void => {
    this.setState({
      productSearchQuery: this.tableViewModel.productSearchQuery,
      isProductsSearching: this.tableViewModel.isProductsSearching,

      isShowSearchFinished: this.tableViewModel.isShowSearchFinished,
      items: this.tableViewModel.items,

      displayingItems: this.tableViewModel.displayingItems,
      lastSearchCountry: this.tableViewModel.lastSearchCountry,

      bestSellers: this.tableViewModel.bestSellers,
    });
  };

  public render = (): JSX.Element => {
    const {
      productSearchQuery,
      isProductsSearching,

      isShowSearchFinished,
      items,

      displayingItems,
      selectedImage,
      lastSearchCountry,

      bestSellers,
    } = this.state;

    const tableSchema: TableSchema = {
      url_photo_big: {
        readableName: "Image",
      },
      title: {
        readableName: "Product Title",
      },
      category: {
        readableName: "Category",
      },
      rank_category: {
        readableName: "Rank",
      },
      price: {
        readableName: "Price",
      },
      brand: {
        readableName: "Brand",
      },
      total_global_ratings: {
        readableName: "Global Ratings",
      },
      number_of_results: {
        readableName: "Number of Results",
      },
      number_sellers_keyword: {
        readableName: "Sellers",
      },
      estimated_sales: {
        readableName: "Estimated Monthly Sales",
      },
      estimated_revenue: {
        readableName: "Estimated Monthly Revenue",
      },
      variations: {
        readableName: "Number of Variations",
      },
      weight: {
        readableName: "Weight and Dimensions",
      },
    };

    const renderRow = (columns: string[], item: ProductItem): JSX.Element => (
      <tr className="text-sm" key={`tr_${item.id}`}>
        {columns.map((column) => {
          let columnMinWidth: string | undefined = "200px";
          let columnMaxWidth: string | undefined;

          if (column === "url_photo_big") columnMinWidth = "100px";
          if (column === "title") columnMinWidth = "260px";
          if (column === "category") columnMinWidth = "120px";
          if (column === "price") columnMinWidth = "70px";
          if (column === "brand") columnMinWidth = "70px";
          if (column === "rank_category") columnMinWidth = "70px";
          if (column === "total_global_ratings") columnMinWidth = "80px";

          if (column === "number_of_results") columnMinWidth = "100px";
          if (column === "number_of_results") columnMaxWidth = "160px";

          if (column === "number_sellers_keyword") columnMinWidth = "80px";

          if (column === "estimated_sales") columnMinWidth = "50px";
          if (column === "estimated_sales") columnMaxWidth = "100px";

          if (column === "estimated_revenue") columnMinWidth = "50px";
          if (column === "estimated_revenue") columnMaxWidth = "100px";

          if (column === "variations") columnMinWidth = "120px";
          if (column === "variations") columnMaxWidth = "160px";

          if (column === "weight") columnMinWidth = "140px";

          if (column === "url_photo_big") {
            const imageUrl = item[column];
            if (imageUrl) {
              return (
                <td
                  key={`td_${column}_${item.id}`}
                  style={{ minWidth: columnMinWidth, cursor: "pointer", maxWidth: columnMaxWidth }}
                  className="d-flex justify-content-center h-100"
                  onMouseEnter={(): void => {
                    this.setState({ selectedImage: imageUrl });
                  }}
                  onMouseLeave={(): void => {
                    this.setState({ selectedImage: undefined });
                  }}
                >
                  <img src={imageUrl} loading="lazy" width={95} height={95} />
                </td>
              );
            }

            return (
              <td
                key={`td_${column}_${item.id}`}
                style={{ minWidth: columnMinWidth, maxWidth: columnMaxWidth, color: "#1e1f1d" }}
              >
                N/A
              </td>
            );
          }

          if (column === "price" || column === "estimated_revenue") {
            return (
              <td key={`td_${column}_${item.id}`} style={{ minWidth: columnMinWidth, maxWidth: columnMaxWidth }}>
                <button type="button" className="btn btn-link btn-sm p-0 m-0 text-left" style={{ color: "#1e1f1d" }}>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    item[column]
                      ? `${lastSearchCountry?.currency}${StringUtils.getNumberWithHundredthsZeros(
                          Number(item[column])
                        )}`
                      : "N/A"
                  }
                </button>
              </td>
            );
          }

          if (column === "title") {
            return (
              <td
                key={`td_${column}_${item.id}`}
                style={{ minWidth: columnMinWidth, maxWidth: columnMaxWidth, wordWrap: "break-word" }}
              >
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 m-0 text-left"
                  style={{ color: "#1e1f1d" }}
                  onClick={(): void => {
                    const win = window.open(item.link, "_blank");
                    if (win) {
                      win.focus();
                    }
                  }}
                >
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    item[column] || "N/A"
                  }
                </button>
              </td>
            );
          }

          return (
            <td key={`td_${column}_${item.id}`} style={{ minWidth: columnMinWidth }}>
              <button type="button" className="btn btn-link btn-sm p-0 m-0 text-left" style={{ color: "#1e1f1d" }}>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  item[column] || "N/A"
                }
              </button>
            </td>
          );
        })}
      </tr>
    );

    return (
      <>
        <div id="dashboard-background" />

        <NavbarComponent key="main-navbar" adminNavbarViewModel={this.props.navbarViewModel} />

        <div className="container-fluid">
          <div className="row justify-content-center my-5">
            <div className="col-11">
              {/* TOP PRODUCTS LINE */}
              <div className="row mb-5">
                <div className="scroll-left flex-grow-1">
                  <p>
                    {bestSellers.map((bestSeller, index) => {
                      return (
                        <React.Fragment key={`${bestSeller.link}_${bestSeller.title}`}>
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={bestSeller.link}
                            style={{ color: "#223340" }}
                          >
                            {bestSeller.title.split(" ").splice(0, 6).join(" ")}
                          </a>
                          {index - 1 !== bestSellers.length && <>&nbsp;...&nbsp;</>}
                          {(index + 1) % 3 === 0 && " Amazon Best Sellers ... "}
                        </React.Fragment>
                      );
                    })}
                  </p>
                </div>
              </div>

              {/* SEARCH LINE */}
              <div className={`row justify-content-center ${displayingItems.length === 0 ? "align-center-line" : ""}`}>
                <div className="col-1 col-md-2 col-lg-3 col-xl-4" />
                <div className="col-10 col-md-8 col-lg-6 col-xl-4">
                  <div className="row">
                    <div className="flex-grow-1">
                      <input
                        style={{ height: "42px" }}
                        type="text"
                        placeholder="Enter product name..."
                        className="form-control"
                        onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>): void => {
                          // Enter press
                          if (e.charCode === 13) {
                            this.tableViewModel.onSearchProduct();
                          }
                        }}
                        value={productSearchQuery}
                        onChange={(e): void => this.tableViewModel.onProductSearchQueryChanged(e.target.value)}
                      />
                    </div>

                    <button
                      type="button"
                      className="btn btn-success ml-1 d-flex justify-content-center align-items-center mr-1"
                      style={{ width: "42px", height: "42px" }}
                      onClick={(): void => this.tableViewModel.onSearchProduct()}
                      disabled={isProductsSearching}
                    >
                      {isProductsSearching ? (
                        <LoadingComponent color="light" size="xs" />
                      ) : (
                        <FontAwesomeIcon icon="search" />
                      )}
                    </button>
                    {items.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-success ml-1 d-flex justify-content-center align-items-center mr-1"
                        onClick={(): void => this.tableViewModel.onGenerateSearch()}
                        style={{ minHeight: "42px", fontSize: "16px" }}
                        disabled={isProductsSearching}
                      >
                        Related Trending Products
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-1 col-md-2 col-lg-3 col-xl-4">
                  <div className="row">
                    <button
                      type="button"
                      className="btn btn-success ml-auto"
                      onClick={(): void => this.tableViewModel.onSearchRandomProducts()}
                      disabled={isProductsSearching}
                      style={{ minHeight: "42px", fontSize: "16px" }}
                    >
                      Random Niches
                    </button>
                  </div>
                </div>
              </div>
              {/* END OF SEARCH LINE */}
            </div>
          </div>

          {items.length > 0 && (
            <div className="row justify-content-center mb-5">
              <div className="col-11">
                <Table
                  tableSchema={tableSchema}
                  items={displayingItems}
                  columns={Object.keys(tableSchema)}
                  totalItemsCount={items.length}
                  fetchItems={this.tableViewModel.fetchItems}
                  fetchItemsWithCallback={(): void => {
                    throw new Error("Function is not supported");
                  }}
                  onSortColumn={this.tableViewModel.onSortColumn}
                  renderRow={renderRow}
                />
              </div>
            </div>
          )}
        </div>

        <FooterComponent />

        <div style={{ position: "fixed", bottom: "30px", right: "30px" }}>
          <Toast
            show={isShowSearchFinished}
            onClose={(): void => this.tableViewModel.onCloseSearchFinished()}
            delay={2000}
            autohide
            className="toast-success bg-success text-white"
          >
            <Toast.Body>Search has been finished</Toast.Body>
          </Toast>
        </div>

        <Modal
          show={!!selectedImage}
          onHide={(): void => {
            this.setState({ selectedImage: undefined });
          }}
          style={{ backgroundColor: "transparent", background: "transparent", marginTop: "100px" }}
        >
          <Modal.Body
            className="d-flex justify-content-center align-items-center"
            style={{ width: "390px", height: "390px" }}
          >
            <img
              src={selectedImage}
              className="product-image-open"
              style={{ cursor: "pointer" }}
              onClick={(): void => this.setState({ selectedImage: undefined })}
            />
          </Modal.Body>
        </Modal>
      </>
    );
  };
}
